import React from "react";
import { Table } from "../../../components/Table";
import { AdmobApp } from "../interface";
import { useTableColumns } from "./TableColumns";
import { DEFAULT_ITEMS_PER_PAGE } from "../../../shared/constants";
import { BannerNote } from "../../../components/BannerNote";

interface ReportsTableProps {
  data: AdmobApp[];
  isLoading: boolean;
  handleCountryClick: (
    countryCode: string,
    countryName: string,
    app_package_id: string
  ) => void;
  t: (key: string) => string;
  navigate: (path: string) => void;
  showPagination?: boolean;
  isMobile?: boolean;
  hasCampaignBidManagementPermission: boolean;
  hasCountryCostReadPermission: boolean;
  hasAppDetailsReadPermission: boolean;
  enabledColumnIds: string[];
  withTax: boolean;
  hasAppChartReadPermission: boolean;
  onChartClick?: (app: AdmobApp) => void;
  columnIds: { [key: string]: string };
  currentLanguage: string;
  growthIndicatorRange: number[];
}

export const ReportsTable = React.memo(
  ({
    data,
    isLoading,
    t,
    handleCountryClick,
    navigate,
    showPagination,
    isMobile,
    hasCampaignBidManagementPermission,
    hasCountryCostReadPermission,
    hasAppDetailsReadPermission,
    enabledColumnIds,
    hasAppChartReadPermission,
    onChartClick,
    columnIds,
    growthIndicatorRange,
  }: ReportsTableProps) => {
    const columns = useTableColumns({
      t,
      onCampaignCostClick: handleCountryClick,
      navigate: navigate,
      hasCampaignBidManagementPermission,
      hasCountryCostReadPermission,
      hasAppDetailsReadPermission,
      enabledColumnIds,
      hasAppChartReadPermission,
      onChartClick: onChartClick || (() => { }),
      columnIds,
      growthIndicatorRange,
    });

    // Build summaryKeys based on enabled columns
    const summaryKeys = [
      { key: columnIds.APP_NAME, customValue: "SUMMARY" },
      { key: columnIds.ESTIMATE_REVENUE },
      { key: columnIds.IN_APP_PURCHASES_OR_SUBSCRIPTIONS },
      { key: columnIds.COST_DOLLAR },
      { key: columnIds.COST_INR },
      { key: columnIds.NET_DOLLAR },
      { key: columnIds.COST_DOLLAR_WITH_TAX },
      { key: columnIds.COST_INR_WITH_TAX },
      { key: columnIds.NET_DOLLAR_WITH_TAX },
    ];

    // Pin columns list
    const leftPinnedColumnIds = [columnIds.APP_NAME];

    return (
      <>
        <Table
          data={data}
          columns={columns}
          isLoading={isLoading}
          showPagination={showPagination}
          showSearch={false}
          loadingHeight={4}
          loadingRowCount={5}
          pageSize={DEFAULT_ITEMS_PER_PAGE}
          maxHeight="h-full"
          className="mt-2"
          isMobile={isMobile}
          summaryKeys={summaryKeys}
          summaryPosition="bottom"
          sortByKey={columnIds.ESTIMATE_REVENUE}
          sortOrder="desc"
          leftPinnedColumnIds={leftPinnedColumnIds}
        />
        <BannerNote
          note={t("common.googleDataNote")}
          title={t("common.dataSourceInfo")}
          marqueeSpeed={25}
          delay={3}
          rootClassName="mt-2"
        />
      </>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.data === nextProps.data &&
      prevProps.isLoading === nextProps.isLoading &&
      prevProps.showPagination === nextProps.showPagination &&
      prevProps.isMobile === nextProps.isMobile &&
      prevProps.enabledColumnIds === nextProps.enabledColumnIds &&
      prevProps.withTax === nextProps.withTax &&
      prevProps.hasAppChartReadPermission ===
      nextProps.hasAppChartReadPermission &&
      prevProps.onChartClick === nextProps.onChartClick &&
      prevProps.handleCountryClick === nextProps.handleCountryClick &&
      prevProps.currentLanguage === nextProps.currentLanguage
    );
  }
);
